import {
  Button,
  CircularSpinnerß,
  cx,
  Tooltipß,
} from '@otter-ai/den-components';
import { WarningTriangleIcon } from '@otter-ai/den-icons';
import { useState } from 'react';
import { SECOND_MS } from '@/constants/time';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';

const ERROR_STATE_DURATION_MS = 2 * SECOND_MS;

export type DisconnectedButtonProps = {
  /**
   * The text for the disconnected button.
   * @example
   * 'Disconnect'
   * t('disconnectButton')
   */
  disconnectButtonText: string;
  disconnectAction: () => Promise<boolean>;
  /**
   * The text of the tooltip that appears when hovering over the button.
   *
   * @example
   * 'Disconnect {accountName} {appName} account from Otter.ai'
   * t('disconnectTooltip', { accountName, appName })
   */
  tooltipCaption: string;
  /**
   * The data-testid for the button.
   * @example
   * 'app-disconnect-google'
   */
  'data-testid'?: string;
};

/**
 * The UI for the disconnected button for an app.
 * - Shows a spinner while disconnecting.
 * - Momentarily shows an error icon if the disconnect action fails.
 *
 * @example
 * ```tsx
 * // The disconnect action should return true if the
 * // disconnect was successful, false otherwise.
 * const disconnectAction = async () => {
 *   try {
 *     await disconnectAppAsync({ service: BUILT_IN_APPS.DROPBOX, account });
 *     addToast(<SuccessToast description={t('successfulDisconnected')} />);
 *     return true;
 *   } catch {
 *     addToast(<ErrorToast description={t('failedDisconnect')} />);
 *     return false;
 *   }
 * }
 *   await disconnectAppAsync({ service: BUILT_IN_APPS.DROPBOX, account });
 * }
 *
 * // ...
 * <DisconnectedButton
 *   disconnectButtonText={t('disconnectButton')}
 *   disconnectAction={() => disconnectAction()}
 *   tooltipCaption={t('disconnectTooltip', { accountName })}
 * />
 * ```
 */
export function DisconnectedButton({
  disconnectButtonText,
  disconnectAction,
  tooltipCaption,
  'data-testid': dataTestId,
}: DisconnectedButtonProps) {
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showError, setShowError] = useState(false);
  useReducedMotion();
  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    const success = await disconnectAction();
    setIsDisconnecting(false);
    if (!success) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, ERROR_STATE_DURATION_MS);
    }
  };
  return (
    <Tooltipß caption={tooltipCaption}>
      <Button
        variant={showError ? 'negative' : 'outlineNeutral'}
        version="legacy"
        onClick={handleDisconnect}
        disabled={isDisconnecting}
        className="relative motion-safe:transition-colors motion-safe:duration-300"
        data-testid={dataTestId}
      >
        <span
          className={cx(
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-300',
            !isDisconnecting && !showError && 'opacity-100',
          )}
        >
          {disconnectButtonText}
        </span>
        <span
          hidden={!isDisconnecting}
          className={cx(
            'motion-safe:transition-opacity motion-safe:duration-300 absolute left-1/2 -translate-x-1/2',
            isDisconnecting && 'opacity-100',
          )}
        >
          <CircularSpinnerß size="xxsm" data-testid="disconnect-app-loading" />
        </span>
        <span
          hidden={!showError}
          className={cx(
            'motion-safe:transition-opacity motion-safe:duration-300 absolute left-1/2 -translate-x-1/2',
            showError && 'opacity-100',
          )}
        >
          <WarningTriangleIcon size={20} />
        </span>
      </Button>
    </Tooltipß>
  );
}
